import React, { useRef, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Message from '../chat/Message';
import TypingIndicator from '../chat/TypingIndicator';
import sendIcon from '../../assets/icons/send.svg'
import anneFrankAvatar from '../../assets/icons/annefrankAvatar.webp'
import { palette } from '../../themes/theme';
import { useParams } from 'react-router-dom';
import { ANNEFRANK_CHAT_URL, AGENT_URL } from '../../config';
import { saveChatMessage, getSessionId } from '../../firebase/repositories/ChatRepository';

interface ChatMessage {
    author: string;
    content: string | React.ReactNode;
    audio?: string;
    followups?: string[];
}

interface ChatWidgetProps {
    mode?: 'default' | 'annefrank';
    stationContext?: string;
    stationId?: string;
    stationName?: string;
    appSlug?: string;
    language?: string;
    welcomeMessage?: string;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ mode = 'default', stationContext, stationId, stationName, appSlug, language, welcomeMessage }) => {
    const { t } = useTranslation()
    const { placeId: routePlaceId } = useParams()
    const MessagesWindowRef = useRef<HTMLUListElement | null>(null)

    const isAnneFrank = mode === 'annefrank';

    const defaultWelcome = "Hi there! 👋 I'm your virtual guide. I'm here to help you explore this exhibit. Got any questions or want to know more? Just ask away, and I'll be happy to assist";

    const [message, setMessage] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            author: 'agent',
            content: welcomeMessage || defaultWelcome,
        }
    ])

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    function scrollToBottom () {
        MessagesWindowRef.current?.scrollTo({
          top: MessagesWindowRef.current.scrollHeight,
          behavior: 'smooth',
        });
    }

    const sendMessage = async (text: string) => {
        if (text.trim() === '') return;

        const newMessage: ChatMessage = { author: 'me', content: text };
        const typingMessage: ChatMessage = { author: 'agent', content: <TypingIndicator /> };

        setMessages(prev => [...prev, newMessage, typingMessage]);
        setMessage('');
        setIsLoading(true);

        try {
            let agentResponse: ChatMessage;

            if (isAnneFrank) {
                const response = await fetch(ANNEFRANK_CHAT_URL!, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        message: text.trim(),
                        context: stationContext || '',
                        sessionId: getSessionId(),
                    })
                });

                if (!response.ok) {
                    throw new Error(`API error: ${response.status}`);
                }

                const data = await response.json();
                agentResponse = {
                    author: 'agent',
                    content: data.message,
                    followups: data.followups || [],
                };

                saveChatMessage({
                    appSlug: appSlug || '',
                    language: language || '',
                    stationId: stationId || '',
                    stationName: stationName || '',
                    userMessage: text,
                    agentReply: data.message,
                }).catch((err) => console.error('[ChatWidget] saveChatMessage failed:', err));
            } else {
                const response = await fetch(AGENT_URL!, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        spot: `station_${routePlaceId}`,
                        audience: 'default',
                        question: text.trim(),
                    })
                });
                if (!response.ok) throw new Error(`Agent API error: ${response.status}`);
                const data = await response.json();
                agentResponse = {
                    author: 'agent',
                    content: data.response,
                    audio: data.audio_url,
                };
            }

            setMessages(prev => [...prev.slice(0, -1), agentResponse]);
        }
        catch (err) {
            Sentry.captureException(err);
            setMessages(prev => [
                ...prev.slice(0, -1),
                { author: 'agent', content: 'Sorry, something went wrong.' }
            ]);
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleSubmitMessage = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        await sendMessage(message);
    };

    const handleFollowupClick = (text: string) => {
        if (!isLoading) {
            sendMessage(text);
        }
    };

    const lastAgentIndex = messages.reduce((last, msg, i) => msg.author === 'agent' ? i : last, -1);

    return (
        <div className='chat-page' style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <List ref={MessagesWindowRef} sx={{ overflowY: 'auto', flex: 1 }}>
                    {
                        messages.map((msg, index) => (
                            <ListItem key={index} dir={msg.author === 'me'? 'rtl' : 'ltr'} sx={{ paddingLeft: 0, paddingRight: '1rem'}}>
                                <Message
                                    author={msg.author}
                                    content={msg.content}
                                    audio={msg.audio}
                                    followups={msg.followups}
                                    onFollowupClick={handleFollowupClick}
                                    isLatestAgent={index === lastAgentIndex}
                                    isLoading={isLoading}
                                    agentAvatar={isAnneFrank ? anneFrankAvatar : undefined}
                                    fontSize={isAnneFrank ? '16px' : undefined}
                                    hideUserIcon={isAnneFrank}
                                />
                            </ListItem>
                        ))
                    }
                </List>

            {isAnneFrank && (
                <Typography
                    variant="caption"
                    dir="auto"
                    sx={{
                        textAlign: 'center',
                        color: palette.text.secondary,
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '11px',
                        px: '1rem',
                        pt: '0.5rem',
                    }}
                >
                    {t('common.anne-frank-chat.ai-disclaimer')}
                </Typography>
            )}

            <Box
                component='form'
                autoComplete='off'
                onSubmit={handleSubmitMessage}
                sx={{
                    display: 'flex',
                    gap: '1rem',
                    pl: '1rem', pr: '0.7rem',
                    alignItems: 'end'
                }}
            >
                <TextField
                    value={message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMessage(e.target.value)}
                    disabled={isLoading}
                    sx={{
                        flex: '1',
                        pt: isAnneFrank ? '1rem' : '2.9rem',
                        '& .MuiInputBase-root': {
                            backgroundColor: isAnneFrank ? palette.background.cream : palette.surface.inputBg,
                            opacity: isAnneFrank ? 1 : '40%',
                            borderRadius: '3rem'
                        },
                        '& .MuiInputBase-input': {
                            fontFamily: isAnneFrank ? 'Poppins, sans-serif' : 'SimplerPro',
                            fontSize: '16px',
                            p: '1.4rem'
                        }
                    }}
                />
                <IconButton
                    type='submit'
                    aria-label='send'
                    disableRipple={true}
                    disabled={isLoading || message.trim() === ''}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSubmitMessage(e)}
                    sx={{
                        height: '5.1rem',
                        width: '5.1rem',
                        backgroundColor: isAnneFrank ? palette.brand.gold : palette.brand.teal,
                        '&:disabled': {
                            backgroundColor: isAnneFrank ? palette.brand.gold : palette.brand.teal,
                            opacity: 0.5,
                        },
                    }}
                >
                    <img src={sendIcon} alt='Send' style={{width: '58%', height: '57%', borderRadius: 'unset'}}/>
                </IconButton>
            </Box>
        </div>
    );
};

export default ChatWidget;
